﻿import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Modal } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton, DemoBadge, TableLoadingRow } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { paymentJobsApi } from '@/services/paymentJobs/api'
import type { PaymentJobResponse } from '@/services/paymentJobs/types'
import { isHandledAuthError } from '@/services/client'
import { CASE_STATUS_CODE_MAP, type CaseStatus } from '@/constants/enums'
import useSearchPersistence from '@/hooks/useSearchPersistence'

function parsePositiveInt(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

const EMPTY_DISPLAY = '-'

// 支給者一覧（クライアント側の絞り込み）の最後に適用した検索条件を localStorage に保持する
// （顧客要望：「検索条件は最後に選択した結果を保持する」）。ページング・ソートは無い画面のため条件のみ。
// 他画面と同様にフラットなキーを用い、支給者詳細から戻った際に直前の絞り込みを復元する。
interface PaymentJobDetailSearch {
  identId: string
  name: string
  householdNo: string
}

const DEFAULT_SEARCH: PaymentJobDetailSearch = {
  identId: '',
  name: '',
  householdNo: '',
}

const PaymentJobDetail = () => {
  const navigate = useAppNavigate()
  const { jobId: jobIdParam } = useParams<{ jobId?: string }>()
  const [job, setJob] = useState<PaymentJobResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [savedSearch, setSavedSearch] = useSearchPersistence<PaymentJobDetailSearch>('payment-job-detail', DEFAULT_SEARCH)
  const [qIdentId, setQIdentId] = useState(savedSearch.identId)
  const [qName, setQName] = useState(savedSearch.name)
  const [qHouseholdNo, setQHouseholdNo] = useState(savedSearch.householdNo)
  const [applied, setApplied] = useState({
    identId: savedSearch.identId,
    name: savedSearch.name,
    householdNo: savedSearch.householdNo,
  })

  const effectiveJobId = parsePositiveInt(jobIdParam ?? null)

  const loadJob = useCallback(async () => {
    if (!effectiveJobId) return

    setLoading(true)
    try {
      const { data } = await paymentJobsApi.getById(effectiveJobId)
      setJob(data)
    } catch (error) {
      if (isHandledAuthError(error)) return
      navigate('payment-job')
    } finally {
      setLoading(false)
    }
  }, [effectiveJobId, navigate])

  useEffect(() => {
    if (!effectiveJobId) {
      Modal.error({ content: '表示する支払リストが選択されていません。' })
      navigate('payment-job')
      return
    }
    // 支払リスト詳細を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadJob()
  }, [effectiveJobId, loadJob, navigate])

  const filteredRows = useMemo(() => {
    const rows = job?.recipients ?? []
    return rows.filter((row) => {
      if (applied.identId && !String(row.identId ?? '').toLowerCase().includes(applied.identId.toLowerCase())) {
        return false
      }
      if (applied.name && !String(row.name ?? '').includes(applied.name)) {
        return false
      }
      if (applied.householdNo && !String(row.householdNo ?? '').includes(applied.householdNo)) {
        return false
      }
      return true
    })
  }, [applied, job?.recipients])

  const runSearch = () => {
    const nextApplied = {
      identId: qIdentId.trim(),
      name: qName.trim(),
      householdNo: qHouseholdNo.trim(),
    }
    setApplied(nextApplied)
    setSavedSearch(nextApplied)
    void loadJob()
  }

  const runClear = () => {
    setQIdentId('')
    setQName('')
    setQHouseholdNo('')
    const clearedApplied = {
      identId: '',
      name: '',
      householdNo: '',
    }
    setApplied(clearedApplied)
    setSavedSearch(clearedApplied)
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '支給者管理', page: 'payment-job' }, { label: '支給者一覧' }]} />
      <div className="mx-auto max-w-7xl px-6 py-8">
        <BackButton onClick={() => navigate('payment-job')} />

        {job && (
          <div className="mb-4 rounded-xl bg-white p-4 text-sm text-gray-700 shadow-lg">
            <div>
              <span className="font-mono font-medium">{job.jobNo}</span>
              <span className="mx-2 text-gray-300">|</span>
              <span>
                振込指定日: <span className="font-medium">{job.transferDate || EMPTY_DISPLAY}</span>
              </span>
              <span className="mx-2 text-gray-300">|</span>
              <span>{job.jobName}</span>
            </div>
          </div>
        )}

        <div className="mb-6 rounded-xl bg-white p-6 shadow-lg">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-bold text-gray-800">検索条件</h2>
            <span className="shrink-0 text-xs text-gray-500">AND検索</span>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <input
              type="text"
              value={qIdentId}
              onChange={(event) => setQIdentId(event.target.value)}
              placeholder="識別ID"
              className="rounded-lg border border-gray-300 px-4 py-2"
            />
            <input
              type="text"
              value={qName}
              onChange={(event) => setQName(event.target.value)}
              placeholder="氏名"
              className="rounded-lg border border-gray-300 px-4 py-2"
            />
            <input
              type="text"
              value={qHouseholdNo}
              onChange={(event) => setQHouseholdNo(event.target.value)}
              placeholder="世帯番号"
              className="rounded-lg border border-gray-300 px-4 py-2"
            />
          </div>
          <div className="flex justify-end">
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={runClear}
                className="cursor-pointer rounded-lg border border-gray-300 px-6 py-2 transition hover:bg-gray-50"
              >
                クリア
              </button>
              <button
                type="button"
                onClick={runSearch}
                className="cursor-pointer rounded-lg bg-blue-600 px-6 py-2 text-white shadow-md transition hover:bg-blue-700"
              >
                検索
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg">
          <h2 className="mb-4 text-xl font-bold text-gray-800">検索結果（{filteredRows.length}件）</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="border-b-2 border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">識別ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">世帯番号</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">氏名</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">給付金額</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">ステータス</th>
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {filteredRows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={5} />
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id} className="transition hover:bg-blue-50">
                      <td className="px-4 py-3 text-sm font-mono">
                        {row.identId ? (
                          <button
                            type="button"
                            onClick={() =>
                              navigate('recipient-detail', {
                                identId: row.identId,
                                detailPageTitle: '受給者情報詳細',
                                baseCrumb: { label: '支給者一覧', page: 'payment-job-detail', navigateOpts: { paymentJobId: effectiveJobId ?? undefined } },
                              })
                            }
                            className="cursor-pointer text-blue-600 hover:underline"
                          >
                            {row.identId}
                          </button>
                        ) : (
                          EMPTY_DISPLAY
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{row.householdNo || EMPTY_DISPLAY}</td>
                      <td className="px-4 py-3 text-sm">{row.name || EMPTY_DISPLAY}</td>
                      <td className="px-4 py-3 text-left text-sm">
                        {(() => {
                          const code = row.caseStatusCode as CaseStatus | null | undefined
                          const calculated =
                            code != null && CASE_STATUS_CODE_MAP[code] != null
                              ? CASE_STATUS_CODE_MAP[code] >= CASE_STATUS_CODE_MAP.CALC_DONE
                              : false
                          if (!calculated || row.amount == null) return EMPTY_DISPLAY
                          return row.amount.toLocaleString()
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {row.caseStatusLabel ? <DemoBadge status={row.caseStatusLabel} /> : EMPTY_DISPLAY}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentJobDetail
